import React, { useState } from 'react';
import './GameSetup.css';

interface GameSetupProps {
  gameType: 'classic-ludo' | 'naija-ludo' | 'checkers';
  onQuickPlay: () => void;
  onComputerGame: (difficulty: 'easy' | 'medium' | 'hard') => void;
  onBack: () => void;
}

const GameSetup: React.FC<GameSetupProps> = ({
  gameType,
  onQuickPlay,
  onComputerGame,
  onBack,
}) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState<
    'easy' | 'medium' | 'hard'
  >('medium');

  const gameInfo = {
    'classic-ludo': {
      name: 'Classic Ludo',
      icon: '🎲',
      description: 'Traditional Ludo with single dice roll',
    },
    'naija-ludo': {
      name: 'Naija Ludo',
      icon: '🎲🎲',
      description: 'Nigerian style with dual dice',
    },
    checkers: {
      name: 'Checkers',
      icon: '⚫',
      description: 'Jump and capture strategy game',
    },
  };

  const difficultyInfo = {
    easy: {
      icon: '😊',
      description: 'Computer makes random moves - perfect for learning',
    },
    medium: {
      icon: '🤔',
      description: 'Computer uses basic strategy - good challenge',
    },
    hard: {
      icon: '🧠',
      description: 'Computer plays optimally - serious challenge!',
    },
  };

  const currentGame = gameInfo[gameType];

  return (
    <div className="game-setup">
      <div className="game-setup-header">
        <button onClick={onBack} className="back-btn">
          ← Back to Games
        </button>
        <h1>
          {currentGame.icon} {currentGame.name}
        </h1>
        <p>{currentGame.description}</p>
      </div>

      <div className="setup-options">
        {/* Quick Play Section */}
        <div className="setup-card quick-play-card">
          <div className="card-header">
            <span className="card-icon">⚡</span>
            <h3>Quick Play</h3>
          </div>
          <div className="card-content">
            <p>Jump into the first available game with other players</p>
            <ul className="feature-list">
              <li>✨ Instant matching</li>
              <li>👥 Real human opponents</li>
              <li>🎯 4-player games</li>
              <li>🏆 Skill points & achievements</li>
            </ul>
          </div>
          <button className="play-button primary" onClick={onQuickPlay}>
            🚀 Find Game Now
          </button>
        </div>

        {/* Computer Game Section */}
        <div className="setup-card computer-card">
          <div className="card-header">
            <span className="card-icon">🤖</span>
            <h3>Play vs Computer</h3>
          </div>
          <div className="card-content">
            <p>Practice against AI opponents with adjustable difficulty</p>

            <div className="difficulty-selector">
              <h4>Choose Difficulty:</h4>
              <div className="difficulty-options">
                {(
                  Object.keys(difficultyInfo) as Array<
                    keyof typeof difficultyInfo
                  >
                ).map((difficulty) => (
                  <div
                    key={difficulty}
                    className={`difficulty-option ${selectedDifficulty === difficulty ? 'selected' : ''}`}
                    onClick={() => setSelectedDifficulty(difficulty)}
                  >
                    <span className="difficulty-icon">
                      {difficultyInfo[difficulty].icon}
                    </span>
                    <span className="difficulty-name">
                      {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                    </span>
                    <span className="difficulty-desc">
                      {difficultyInfo[difficulty].description}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <ul className="feature-list">
              <li>🎯 1 vs 3 AI opponents</li>
              <li>⚡ Instant start</li>
              <li>📚 Perfect for learning</li>
              <li>🎖️ Still earn skill points</li>
            </ul>
          </div>
          <button
            className="play-button secondary"
            onClick={() => onComputerGame(selectedDifficulty)}
          >
            🎮 Start vs{' '}
            {selectedDifficulty.charAt(0).toUpperCase() +
              selectedDifficulty.slice(1)}{' '}
            AI
          </button>
        </div>
      </div>

      <div className="setup-footer">
        <div className="info-box">
          <h4>🎯 Game Rules</h4>
          <p>
            Same rules apply whether playing against humans or computer
            opponents. All games count toward your skill points and
            achievements!
          </p>
        </div>
      </div>
    </div>
  );
};

export default GameSetup;
